/**
 * CL-CART-01 — the cart domain. Pure functions only: no storage, no network,
 * no React. Every function returns a new cart and never mutates its input.
 *
 * Shape (schema v1):
 *
 *   {
 *     v: 1,
 *     ownerId: '<userId>' | null,      // null = guest cart (D-1b)
 *     updatedAt: '2026-08-12T10:00:00.000Z',
 *     sellers: {
 *       '<authorId>': {
 *         authorId, authorDisplayName, updatedAt,
 *         items: [{ listingId, quantity, addedAt, titleSnapshot,
 *                   priceSnapshotInSubunits, currencySnapshot, imageSnapshotUrl }],
 *       },
 *     },
 *   }
 *
 * The cart is grouped by seller from day one (requirement 9) so that adding a
 * second seller later is a checkout change, not a data migration.
 *
 * The *Snapshot fields are for rendering only — so a row can paint before the
 * listing loads. Money and availability are ALWAYS re-read from the live
 * listing before any total is shown or charged.
 */

import {
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  STOCK_INFINITE_ITEMS,
} from '../types';

export const CART_SCHEMA_VERSION = 1;
export const CART_MAX_ITEMS_PER_SELLER = 25;
export const CART_RETENTION_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * What the listing type allows, turned into the three numbers the cart needs.
 *
 * The order of the checks matters. `infiniteOneItem` has endless stock but still sells one
 * per order, so "does it allow multiple?" has to be asked before "is it infinite?" — the
 * other way round gives an unlimited cap to a listing that can only be bought singly.
 *
 * Two consumers: the Add to cart button, for the cap it stores on the row, and the cart
 * row, for whether to show a quantity control at all. One rule, so they cannot disagree.
 *
 * An unrecognised stock type falls back to one per order. A wrong guess in that direction
 * blocks a sale; the other direction oversells.
 */
export const quantityLimitsFor = (stockType, currentStock) => {
  const allowsMultiple = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(stockType);
  const isInfinite = STOCK_INFINITE_ITEMS.includes(stockType);

  return {
    allowsMultiple,
    maxQuantity: !allowsMultiple ? 1 : isInfinite ? null : currentStock ?? null,
    hasStock: isInfinite ? true : (currentStock ?? 0) > 0,
  };
};

export const emptyCart = () => ({
  v: CART_SCHEMA_VERSION,
  // The buyer, or null for a guest cart. See resolveStoredCart (D-1b).
  ownerId: null,
  updatedAt: null,
  sellers: {},
});

/** Stamp a cart as belonging to a user — called when they log in. */
export const claimCart = (cart, ownerId) => ({
  ...(cart || emptyCart()),
  ownerId: ownerId || null,
});

/**
 * Decide what to do with a cart found in storage, given who is here now (D-1b).
 *
 *   ownerId null             → hand it over. A guest cart is claimable by whoever logs in
 *                              — that IS the guest checkout flow (D-2, AC-26).
 *   ownerId === current user → hand it back.
 *   ownerId is someone else  → DISCARD, including when nobody is logged in now.
 *
 * Needed as well as clearing on logout, because logout is the one moment guaranteed not
 * to run when it matters: a crash, a closed tab or an expired token all skip it.
 */
export const resolveStoredCart = (storedCart, currentUserId) => {
  if (!storedCart) return emptyCart();
  const owner = storedCart.ownerId || null;
  if (owner === null) return storedCart;
  return owner === currentUserId ? storedCart : emptyCart();
};

export const getSellerGroup = (cart, authorId) => cart?.sellers?.[authorId] || null;

/** The row for one listing, or null. Callers need it to say "already in your cart" (AC-3). */
export const findCartItem = (cart, authorId, listingId) =>
  getSellerGroup(cart, authorId)?.items?.find(i => i.listingId === listingId) || null;

export const distinctItemCount = (cart, authorId) =>
  getSellerGroup(cart, authorId)?.items?.length || 0;

export const isSellerCartFull = (cart, authorId) =>
  distinctItemCount(cart, authorId) >= CART_MAX_ITEMS_PER_SELLER;

export const totalItemCount = cart =>
  Object.values(cart?.sellers || {}).reduce(
    (sum, group) => sum + group.items.reduce((n, i) => n + i.quantity, 0),
    0
  );

/** The later of two ISO timestamps, either of which may be null. */
// Not a plain `a > b`: an empty cart's updatedAt is null, and `'2026-…' > null` is false
// in JS, so a plain compare returns null and silently breaks D-3 retention.
const laterIso = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return a > b ? a : b;
};

const withGroup = (cart, authorId, group, nowIso) => {
  const sellers = { ...cart.sellers };
  if (group === null || group.items.length === 0) {
    delete sellers[authorId];
  } else {
    sellers[authorId] = group;
  }
  return { ...cart, sellers, updatedAt: nowIso || cart.updatedAt };
};

export const addItem = (cart, params) => {
  const {
    listingId,
    authorId,
    authorDisplayName,
    quantity = 1,
    // The most this row may hold — the listing's stock. Clastique's listing type is
    // stockType 'oneItem', so it is 1 for every listing today: adding the same item twice
    // must leave the cart at 1, not 2 (AC-3). null means no limit, for a listing type that
    // allows multiples. Callers use findCartItem to tell the buyer it is already there.
    maxQuantity = null,
    titleSnapshot,
    priceSnapshotInSubunits,
    currencySnapshot,
    imageSnapshotUrl,
    nowIso,
  } = params;

  const base = cart || emptyCart();
  const existingGroup = getSellerGroup(base, authorId);
  const existingItems = existingGroup?.items || [];
  const existing = existingItems.find(i => i.listingId === listingId);

  // Raising the quantity of a row we already hold is always allowed, even at
  // the cap — the cap counts distinct listings, not units.
  if (!existing && existingItems.length >= CART_MAX_ITEMS_PER_SELLER) {
    return base;
  }

  const capped = n => (maxQuantity == null ? n : Math.min(n, maxQuantity));

  const items = existing
    ? existingItems.map(i =>
        i.listingId === listingId ? { ...i, quantity: capped(i.quantity + quantity) } : i
      )
    : [
        ...existingItems,
        {
          listingId,
          quantity: capped(quantity),
          // Stored on the row so setQuantity can enforce it without every caller having
          // to remember to pass it. A snapshot, not the truth: Task 6's availability pass
          // re-reads live stock, and AC-13 governs what happens when it has dropped.
          maxQuantity,
          addedAt: nowIso,
          titleSnapshot,
          priceSnapshotInSubunits,
          currencySnapshot,
          imageSnapshotUrl,
        },
      ];

  return withGroup(
    base,
    authorId,
    {
      authorId,
      // Kept when the caller passes none. The seller's name lives on the GROUP and never on
      // a row, so replaying a stored row — which is exactly what AC-12's Undo does — used to
      // rebuild the group with `undefined` here: a blank panel heading and a "Check out with
      // {seller}" button with nothing to name, persisted to storage and the profile.
      authorDisplayName: authorDisplayName ?? existingGroup?.authorDisplayName,
      updatedAt: nowIso,
      items,
    },
    nowIso
  );
};

export const setQuantity = (cart, authorId, listingId, quantity, nowIso) => {
  const group = getSellerGroup(cart, authorId);
  if (!group) return cart;

  // Clamped to the row's own cap. The cart page's quantity control calls this, and
  // without the clamp a buyer could ask for 99 of a one-of-one item and only discover it
  // at checkout.
  const items =
    quantity <= 0
      ? group.items.filter(i => i.listingId !== listingId)
      : group.items.map(i =>
          i.listingId === listingId
            ? {
                ...i,
                quantity: i.maxQuantity == null ? quantity : Math.min(quantity, i.maxQuantity),
              }
            : i
        );

  return withGroup(cart, authorId, { ...group, items, updatedAt: nowIso }, nowIso);
};

export const removeItem = (cart, authorId, listingId, nowIso) =>
  setQuantity(cart, authorId, listingId, 0, nowIso);

export const clearSellerGroup = (cart, authorId, nowIso) => withGroup(cart, authorId, null, nowIso);

/**
 * A seller group's items, always an array and never over the cap (D-4).
 *
 * Array.isArray, not `|| []`: a hand-edited or half-written localStorage value can put a
 * string or an object here, and `"str".slice(0, 25)` does not throw — the TypeError surfaces
 * later inside totalItemCount, during render, on every page, until storage is cleared by
 * hand. The cap is the same rule as addItem's; it lives here so every path shares one copy.
 */
const cappedItems = items =>
  (Array.isArray(items) ? items : [])
    .filter(i => i && typeof i.listingId === 'string')
    .map(normalizedItem)
    .slice(0, CART_MAX_ITEMS_PER_SELLER);

/**
 * A row whose quantity is a positive integer.
 *
 * Storage is JSON that anything can have written, and nothing downstream re-checks the type:
 * `totalItemCount` reduces with `+`, so a quantity of `'2'` concatenates instead of adding
 * and the header badge reads "01". A row with no listingId is dropped by cappedItems above
 * rather than repaired — there is no listing it could refer to.
 */
const normalizedItem = item => {
  const n = Math.floor(Number(item.quantity));
  const quantity = Number.isFinite(n) && n > 0 ? n : 1;
  return quantity === item.quantity ? item : { ...item, quantity };
};

export const mergeCarts = (localCart, remoteCart) => {
  const a = localCart || emptyCart();
  const b = remoteCart || emptyCart();
  const authorIds = [...new Set([...Object.keys(a.sellers), ...Object.keys(b.sellers)])];

  const sellers = authorIds.reduce((acc, authorId) => {
    const ga = getSellerGroup(a, authorId);
    const gb = getSellerGroup(b, authorId);
    // Capped on the single-sided branches too. These used to return the group untouched,
    // which meant the ONE rule mergeCarts is responsible for was skipped in exactly the case
    // where nothing else would apply it — a 60-row group arriving from one side only.
    if (!ga) return { ...acc, [authorId]: { ...gb, items: cappedItems(gb?.items) } };
    if (!gb) return { ...acc, [authorId]: { ...ga, items: cappedItems(ga?.items) } };

    const byListing = new Map(cappedItems(ga.items).map(i => [i.listingId, i]));
    cappedItems(gb.items).forEach(i => {
      const existing = byListing.get(i.listingId);
      // Never silently discard either cart: keep the higher quantity.
      byListing.set(i.listingId, existing && existing.quantity >= i.quantity ? existing : i);
    });

    // The cap holds here too, not just in addItem: two carts of 20 merge to 40, past the
    // 25 cap (D-4) and toward Sharetribe's 50-line-item ceiling. Map keeps insertion
    // order and `ga` went in first, so the local cart's rows are the ones kept.
    const items = cappedItems([...byListing.values()]);

    // CL-NEG-01 requirement 13: a lock is a fact about a transaction on the server, so if
    // EITHER copy knows about it, it holds. Losing it would let the buyer edit a basket a live
    // negotiation is still pricing from.
    const lockedByTransactionId = ga.lockedByTransactionId || gb.lockedByTransactionId;
    const lockMaybe = lockedByTransactionId ? { lockedByTransactionId } : {};

    return { ...acc, [authorId]: { ...ga, ...lockMaybe, items } };
  }, {});

  return {
    v: CART_SCHEMA_VERSION,
    // Carries an existing owner through; the caller stamps the final one with claimCart.
    ownerId: a.ownerId || b.ownerId || null,
    updatedAt: laterIso(a.updatedAt, b.updatedAt),
    sellers,
  };
};

/**
 * Decide how this browser's cart and the copy on the buyer's profile should be combined.
 *
 * `mergeCarts` keeps everything from both sides, and that is right for exactly ONE case: a
 * guest cart meeting a saved cart at login. Those are two genuinely different baskets, and
 * E14 forbids discarding either.
 *
 * It is WRONG for the same person on a second device. There the local cart is not a second
 * basket, it is an out-of-date copy of the same one — so a union resurrects whatever they
 * deleted on the other device. Observed in real use: delete an item in one browser, reload
 * the other, and it comes back; the stale browser then writes it to the profile and undoes
 * the deletion everywhere. The buyer cannot delete anything unless they use one browser.
 *
 * `ownerId` is what tells the two cases apart, and we already store it (D-1b).
 *
 * For the second case E13 already states the rule — "Last write wins" — so the newer of the
 * two carts wins whole. Doing better would need per-item deletion records (tombstones),
 * which is a larger change than this milestone should carry.
 */
export const reconcileCarts = (localCart, remoteCart, currentUserId = null) => {
  const a = localCart || emptyCart();
  const b = remoteCart || emptyCart();

  // A guest cart, or nobody logged in: the login handover. Union, per E14.
  if (!currentUserId || !a.ownerId) return mergeCarts(a, b);

  // Somebody else's cart. loadCart already drops these (D-1b); this is belt and braces, and
  // a union is the safe answer if one ever reaches here.
  if (a.ownerId !== currentUserId) return mergeCarts(a, b);

  // Same person, stale copy. Newer wins whole, so deletions travel.
  const newest = laterIso(a.updatedAt, b.updatedAt);
  if (newest === null) return a;
  // Ties go to this device: it is where the buyer is currently working.
  return newest === b.updatedAt && b.updatedAt !== a.updatedAt ? b : a;
};

/**
 * The one gate every cart from outside this module passes through: localStorage, the
 * buyer's profile, or anywhere else later.
 *
 * It exists because these rules were each enforced on one path and missed on another — a
 * sweep after Task 3 found five such holes, all the same shape. Composing them here means
 * a new source of carts cannot skip a check by forgetting one line.
 *
 * Rejects outright (returns an empty cart): a different schema version, a cart past the
 * retention window (D-3), a cart belonging to someone else (D-1b).
 * Repairs rather than rejects: a group over the per-seller cap is truncated (D-4) —
 * dropping the whole cart would punish a buyer for data they never edited, and the cap
 * exists to stay under Sharetribe's 50-line-item ceiling, which truncating achieves.
 */
export const sanitizeCart = (cart, { nowIso, currentUserId = null }) => {
  if (!cart || cart.v !== CART_SCHEMA_VERSION) return emptyCart();
  if (isCartStale(cart, nowIso)) return emptyCart();

  const owned = resolveStoredCart(cart, currentUserId);
  const groups = Object.entries(owned.sellers || {});
  if (groups.length === 0) return owned;

  return {
    ...owned,
    sellers: Object.fromEntries(
      groups.map(([id, group]) => [id, { ...group, items: cappedItems(group?.items) }])
    ),
  };
};

export const isCartStale = (cart, nowIso) => {
  if (!cart?.updatedAt) return false;
  const age = new Date(nowIso).getTime() - new Date(cart.updatedAt).getTime();
  return age > CART_RETENTION_DAYS * DAY_MS;
};

/**
 * CL-NEG-01 requirement 13 — a seller group with an open offer is read-only.
 *
 * On the GROUP, not the cart: a cart can hold several sellers (CartPage renders one panel each)
 * and an offer covers exactly one of them. A cart-level flag would freeze a seller's rows for a
 * negotiation they are not part of.
 *
 * No CART_SCHEMA_VERSION bump: sanitizeCart spreads unknown group keys through untouched, and an
 * older build simply ignores a key it has never heard of — which degrades to "the lock is not
 * shown", never to "the wrong rows are frozen".
 */
export const lockSellerGroup = (cart, authorId, transactionId, nowIso) => {
  const group = getSellerGroup(cart, authorId);
  if (!group) return cart;
  return withGroup(cart, authorId, { ...group, lockedByTransactionId: transactionId }, nowIso);
};

export const unlockSellerGroup = (cart, authorId, nowIso) => {
  const group = getSellerGroup(cart, authorId);
  if (!group) return cart;
  const { lockedByTransactionId, ...rest } = group;
  return withGroup(cart, authorId, rest, nowIso);
};

export const isSellerGroupLocked = group => !!group?.lockedByTransactionId;

/**
 * Merge a frozen snapshot's rows INTO one seller's group — what Recreate cart needs
 * (requirement 15).
 *
 * `cartReplaced` would take the whole cart with it, discarding every other seller's basket, and
 * `addItem` cannot be used row by row: it takes a live listing, and the rows being restored come
 * from a frozen snapshot on a transaction. `withGroup`'s own empty-group rule applies — restoring
 * nothing into an empty group removes it rather than leaving an empty panel.
 *
 * WHY A MERGE AND NOT A WHOLESALE REPLACE (review fix)
 *
 * The snapshot is the offer's basket, which is NOT always the buyer's basket. A LISTING-mode
 * offer (CL-NEG-01 requirement 1's "Make an offer" on a listing page) snapshots one row and
 * carries the same `sellerId` as any basket the buyer happens to hold with that seller — so a
 * wholesale replace threw away an unrelated three-item basket the offer never touched.
 *
 * The merge is defined so BUNDLE mode is bit-for-bit unchanged. `supersededListingIds` is every
 * listing the SNAPSHOT names, which is not the same list as `items`: Recreate cart drops rows
 * that have since sold, and those must still leave the group rather than survive as "a row the
 * snapshot never mentioned". So the rule is: the offer owns the listings it snapshotted — its
 * restored rows replace them, a sold-out one takes its row away with it — and every other row of
 * the group is none of the offer's business. A bundle offer's group is exactly its snapshot, so
 * nothing is carried through and this IS the old wholesale replace. A listing-mode offer names
 * one listing and leaves the basket standing.
 *
 * @param {Object} cart
 * @param {string} authorId
 * @param {Array} items - the rows to restore (snapshot rows minus the unavailable ones)
 * @param {string} nowIso
 * @param {Array<string>} [supersededListingIds] - every listing the snapshot names; defaults to
 *   the ids in `items`, which is right whenever the caller filtered nothing out.
 */
export const mergeSellerGroupItems = (cart, authorId, items, nowIso, supersededListingIds) => {
  const group = getSellerGroup(cart, authorId);
  const base = group || { authorId, authorDisplayName: null, updatedAt: nowIso, items: [] };
  // The lock is dropped with the rebuild: the negotiation that set it is over, which is the only
  // reason this button is on screen at all.
  const { lockedByTransactionId, ...unlocked } = base;

  const restored = Array.isArray(items) ? items : [];
  const superseded = new Set(
    Array.isArray(supersededListingIds) ? supersededListingIds : restored.map(i => i?.listingId)
  );
  const untouched = (Array.isArray(base.items) ? base.items : []).filter(
    i => !superseded.has(i?.listingId)
  );

  return withGroup(
    cart,
    authorId,
    { ...unlocked, items: cappedItems([...restored, ...untouched]), updatedAt: nowIso },
    nowIso
  );
};

/**
 * Drop exactly the named rows from one seller's group — what a paid offer needs.
 *
 * Checkout used to CLEAR the whole group, which is right for basket checkout (the group IS what
 * was bought) and wrong for a listing-mode offer, where the buyer paid for one listing and the
 * rest of their basket with that seller was never part of the transaction. Naming the rows makes
 * both cases one rule: remove what was actually bought. A bundle offer names every row, so its
 * group empties and `withGroup` removes it — the old behaviour, reached honestly.
 */
export const removeSellerGroupItems = (cart, authorId, listingIds, nowIso) =>
  (Array.isArray(listingIds) ? listingIds : [])
    .filter(Boolean)
    .reduce((acc, listingId) => removeItem(acc, authorId, listingId, nowIso), cart);
